import React, { useEffect } from "react"
import "./Layout.css"
import AldooBoot from "aldoo-ra/Boot"
import Site from "../components/Site"
import CustomerSupportPanel from "../components/CustomerSupportPanel"
import { VariantProvider } from "aldoo-ra/VariantReader/VariantContext.jsx"
import { usePageContext } from "vike-react/usePageContext"

// Local file-based variants (borrowed from aldoo-meet) — layered ON TOP of
// the aldoo-ra built-in variant files. The provider set replaces built-ins
// entirely inside useVariant, so without this merge any variant missing from
// the meet files (e.g. Modal "centered") would resolve to nothing and render
// unstyled/invisible dialogs.
import TypographyVariants from "../components/Variants/Typography.jsx"
import ButtonVariants from "../components/Variants/Button.jsx"
import AccordionVariants from "../components/Variants/Accordion.jsx"
import DropdownSelectVariants from "../components/Variants/DropdownSelect.jsx"
import InputVariants from "../components/Variants/Input.jsx"
import ModalVariants from "../components/Variants/Modal.jsx"
import TypographyBuiltin from "aldoo-ra/Typography/variants.jsx"
import ButtonBuiltin from "aldoo-ra/Button/variants.jsx"
import AccordionBuiltin from "aldoo-ra/Accordion/variants.jsx"
import DropdownSelectBuiltin from "aldoo-ra/DropdownSelect/variants.jsx"
import InputBuiltin from "aldoo-ra/Input/variants.jsx"
import ModalBuiltin from "aldoo-ra/Modal/variants.jsx"

const localVariants = {
  Typography: { ...TypographyBuiltin, ...TypographyVariants },
  Button: { ...ButtonBuiltin, ...ButtonVariants },
  Accordion: { ...AccordionBuiltin, ...AccordionVariants },
  DropdownSelect: { ...DropdownSelectBuiltin, ...DropdownSelectVariants },
  Input: { ...InputBuiltin, ...InputVariants },
  Modal: { ...ModalBuiltin, ...ModalVariants },
}

// Merge SSR/CMS variants with local file variants.
// Local variants fill in any empty or missing values from the server,
// so bad/incomplete CMS data never overrides correct local definitions.
function mergeWithLocalFallback(ssrVariants) {
  const result = {}
  const names = new Set([
    ...Object.keys(ssrVariants || {}),
    ...Object.keys(localVariants),
  ])
  for (const name of names) {
    const server = ssrVariants?.[name] || {}
    const local = localVariants[name] || {}
    const merged = { ...server }
    for (const [key, value] of Object.entries(local)) {
      if (!merged[key]) merged[key] = value
    }
    result[name] = merged
  }
  return result
}

const SCROLL_KEY = "aldoo_scroll_y"

export function Layout({ children }) {
  const pageContext = usePageContext()
  const variants = mergeWithLocalFallback(pageContext.ssrVariants)

  // vike-react's client-side navigation only patches document.title from Vike's
  // declarative `title` config setting, not the <title> JSX rendered in +Head.jsx —
  // so without this, the tab title freezes at whatever was SSR'd on first load.
  useEffect(() => {
    document.title = pageContext.title || "Aldoo Snap"
  }, [pageContext.title])

  useEffect(() => {
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual"
    }

    const saved = localStorage.getItem(SCROLL_KEY)
    if (saved !== null) {
      const target = parseInt(saved, 10)
      const deadline = Date.now() + 3000
      const tryScroll = () => {
        if (document.body.scrollHeight >= target + window.innerHeight) {
          window.scrollTo(0, target)
        } else if (Date.now() < deadline) {
          requestAnimationFrame(tryScroll)
        }
      }
      if (document.readyState === "complete") {
        tryScroll()
      } else {
        window.addEventListener("load", tryScroll, { once: true })
      }
    }

    let ticking = false
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          localStorage.setItem(SCROLL_KEY, String(window.scrollY))
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  return (
    <React.StrictMode>
      <VariantProvider ssrVariants={variants}>
        <AldooBoot />
        <Site>{children}</Site>
        {/* Opens on PubSub "open:customer-support" (footer Support link) */}
        <CustomerSupportPanel />
      </VariantProvider>
    </React.StrictMode>
  )
}
